import type { RumContext, RumRequestEvent, RumRequestPhase, RumRequestsPayload } from './types';
import type { RumRouter } from './session';

/** One settled request as reported by the API client's `onRequestTiming` hook. */
export type RumRequestTiming = {
  /** e.g. "GET" */
  method: string;
  /** OpenAPI path template, ids unsubstituted — never a resolved URL. */
  path: string;
  /** HTTP status; 0 when no response arrived (network failure, abort, timeout). */
  status: number;
  /** performance.now() at request start. */
  startMs: number;
  durationMs: number;
};

export type RumRequestQueueOptions = {
  send: (payload: RumRequestsPayload) => void;
  getContext: () => RumContext;
  /** The page load's session id — shared with the initial_load beacon so events join to it. */
  getSessionId: () => string;
  /** When the initial_load beacon flushed (ms from timeOrigin); null while unsent. */
  getInitialLoadSentAt: () => number | null;
  /** Flush this long after the first unflushed event. */
  flushAfterMs?: number;
  /** Flush immediately once a batch reaches this size. */
  maxBatchSize?: number;
  /** Hard cap on events recorded per page load; extras count as `dropped`. */
  maxEventsPerPageLoad?: number;
};

const DEFAULT_FLUSH_AFTER_MS = 15_000;
const DEFAULT_MAX_BATCH_SIZE = 50;
const DEFAULT_MAX_EVENTS_PER_PAGE_LOAD = 1_000;

export type RumRequestQueue = {
  /** Wire router subscriptions, flush timers, and tab-hide flushing. Call once, before first render. */
  install: (router: RumRouter) => void;
  /** Queue one settled request. No-op until installed (unsampled loads never install). */
  record: (timing: RumRequestTiming) => void;
};

/**
 * Batches per-request timings into `kind: "requests"` beacons — several small
 * batches per page load, unlike the one-shot initial_load session. Each event
 * is phase-attributed at its *start* time: `initial_load` before the
 * initial-load beacon flushed, `transition` inside a router navigation, `idle`
 * otherwise. Phase + ui_version is what turns "release N+1 added a request to
 * this page" into a single query.
 */
export function createRumRequestQueue(options: RumRequestQueueOptions): RumRequestQueue {
  const {
    send,
    getContext,
    getSessionId,
    getInitialLoadSentAt,
    flushAfterMs = DEFAULT_FLUSH_AFTER_MS,
    maxBatchSize = DEFAULT_MAX_BATCH_SIZE,
    maxEventsPerPageLoad = DEFAULT_MAX_EVENTS_PER_PAGE_LOAD,
  } = options;

  let installed = false;
  let router: RumRouter | null = null;
  let queue: RumRequestEvent[] = [];
  let recordedTotal = 0;
  let dropped = 0;
  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  // Only the latest navigation window is kept: a request is attributed to a
  // transition when it *started* inside that window. Requests from an earlier
  // navigation have almost always settled before the next one begins, so a
  // single window covers the real cases without unbounded bookkeeping.
  let navStartMs: number | null = null;
  let navEndMs: number | null = null;

  function phaseAt(startMs: number): RumRequestPhase {
    const initialLoadSentAt = getInitialLoadSentAt();
    if (initialLoadSentAt == null || startMs <= initialLoadSentAt) {
      return 'initial_load';
    }
    if (navStartMs != null && startMs >= navStartMs && (navEndMs == null || startMs <= navEndMs)) {
      return 'transition';
    }
    return 'idle';
  }

  function currentRoutePattern(): string {
    const matches = router?.state.matches;
    // No pathname fallback: raw paths carry ids and become log group keys.
    return matches?.[matches.length - 1]?.routeId ?? 'unknown';
  }

  function flush(): void {
    if (flushTimer != null) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (queue.length === 0 && dropped === 0) {
      return;
    }
    const requests = queue;
    const droppedInBatch = dropped;
    queue = [];
    dropped = 0;

    // Telemetry must never break the app.
    try {
      const payload: RumRequestsPayload = {
        v: 1,
        kind: 'requests',
        session_id: getSessionId(),
        ...getContext(),
        requests,
      };
      if (droppedInBatch > 0) {
        payload.dropped = droppedInBatch;
      }
      send(payload);
    } catch {
      // swallow
    }
  }

  function onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  }

  return {
    install(appRouter) {
      if (installed) {
        return;
      }
      installed = true;
      router = appRouter;

      appRouter.subscribe('onBeforeNavigate', () => {
        navStartMs = performance.now();
        navEndMs = null;
      });
      appRouter.subscribe('onResolved', () => {
        if (navStartMs != null && navEndMs == null) {
          navEndMs = performance.now();
        }
      });

      document.addEventListener('visibilitychange', onVisibilityChange);
    },
    record(timing) {
      if (!installed) {
        return;
      }
      if (recordedTotal >= maxEventsPerPageLoad) {
        // Still arm the flush: with the queue already drained, an unarmed
        // timer would leave the drop count growing silently forever.
        dropped += 1;
        flushTimer ??= setTimeout(flush, flushAfterMs);
        return;
      }
      recordedTotal += 1;

      queue.push({
        endpoint: `${timing.method} ${timing.path}`,
        status: timing.status,
        start_ms: timing.startMs,
        duration_ms: timing.durationMs,
        phase: phaseAt(timing.startMs),
        route: currentRoutePattern(),
      });

      if (queue.length >= maxBatchSize) {
        flush();
        return;
      }
      flushTimer ??= setTimeout(flush, flushAfterMs);
    },
  };
}
